import { createContext, useContext, useMemo, useState, type ReactNode } from "react";

import { EMPTY_LISTING_DRAFT, type ListingDraft } from "@/features/listings/types/listing";

interface SellWizardContextValue {
  draft: ListingDraft;
  updateDraft: (patch: Partial<ListingDraft>) => void;
  resetDraft: () => void;
}

const SellWizardContext = createContext<SellWizardContextValue | null>(null);

/**
 * Holds the in-progress listing draft across all 3 wizard routes
 * (`/sell/media` → `/sell/details` → `/sell/finalize`) — plain
 * `useState`/Context, not Redux: `docs/notes/sprint4/sprint4.0.md`'s own
 * Redux-usage table draws the line at "shared across the app" (auth,
 * theme, socket); a listing draft is scoped to one feature's own route
 * subtree and disappears once submitted, so it belongs in local/feature
 * state, not the global store. Mounted once by `WizardLayout`, wrapping the
 * 3 step pages via `<Outlet/>` — a hard reload resets the draft, which is
 * an accepted tradeoff, not an oversight (nothing here is meant to survive
 * a page reload any more than an un-submitted native form would).
 */
export function SellWizardProvider({ children }: { children: ReactNode }) {
  const [draft, setDraft] = useState<ListingDraft>(EMPTY_LISTING_DRAFT);

  const value = useMemo<SellWizardContextValue>(
    () => ({
      draft,
      updateDraft: (patch) => setDraft((prev) => ({ ...prev, ...patch })),
      resetDraft: () => setDraft(EMPTY_LISTING_DRAFT),
    }),
    [draft],
  );

  return <SellWizardContext.Provider value={value}>{children}</SellWizardContext.Provider>;
}

/**
 * The provider and its consumer hook are one cohesive unit; splitting them
 * into separate files just to satisfy fast-refresh would cost more than an
 * occasional full-reload during this feature's own development.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useSellWizard(): SellWizardContextValue {
  const context = useContext(SellWizardContext);
  if (!context) {
    throw new Error("useSellWizard must be used within a SellWizardProvider");
  }
  return context;
}
